import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Users, Building2, MapPin, CreditCard, AlertTriangle,
  TrendingUp, RefreshCw, Loader2, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import adminService from '../../services/adminService';

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchStats = async () => {
    setIsLoading(true);
    try {
      const data = await adminService.getDashboardStats();
      setStats(data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const statCards = stats ? [
    { label: 'Total Users', value: stats.totalUsers, sub: `${stats.activeUsers} active`, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Organizations', value: stats.totalOrgs, icon: Building2, color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Operators', value: stats.totalOperators, icon: MapPin, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Active Subscriptions', value: stats.activeSubscriptions, icon: CreditCard, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { label: 'Expiring (30 days)', value: stats.expiringSoon, icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50' },
  ] : [];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-muted-foreground mt-1">System overview and key metrics</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchStats} disabled={isLoading}>
            {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Stat Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {statCards.map((card, i) => (
                <Card key={i}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">{card.label}</p>
                        <p className="text-3xl font-bold mt-1">{card.value ?? 0}</p>
                        {card.sub && <p className="text-xs text-muted-foreground mt-1">{card.sub}</p>}
                      </div>
                      <div className={`p-2.5 ${card.bg} rounded-lg`}>
                        <card.icon className={`h-5 w-5 ${card.color}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Expiring Soon */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-orange-500" />
                    Subscriptions Expiring Soon
                  </CardTitle>
                  <CardDescription>Active subscriptions ending within 30 days</CardDescription>
                </CardHeader>
                <CardContent>
                  {!stats?.expiringSoonList?.length ? (
                    <p className="text-sm text-muted-foreground text-center py-6">No subscriptions expiring soon</p>
                  ) : (
                    <div className="space-y-3">
                      {stats.expiringSoonList.map((sub) => (
                        <div key={sub.organizationSubscriptionId} className="flex items-center justify-between p-3 rounded-lg border">
                          <div>
                            <p className="font-medium text-sm">{sub.organizationName}</p>
                            <p className="text-xs text-muted-foreground">{sub.planName}</p>
                          </div>
                          <div className="text-right">
                            <Badge variant={sub.daysLeft <= 7 ? 'destructive' : 'outline'} className="text-xs">
                              {sub.daysLeft}d left
                            </Badge>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(sub.endDate).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Payments */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-500" />
                    Recent Payments
                  </CardTitle>
                  <CardDescription>Last 5 payment transactions</CardDescription>
                </CardHeader>
                <CardContent>
                  {!stats?.recentPayments?.length ? (
                    <p className="text-sm text-muted-foreground text-center py-6">No recent payments</p>
                  ) : (
                    <div className="space-y-3">
                      {stats.recentPayments.map((p) => (
                        <div key={p.paymentHistoryId} className="flex items-center justify-between p-3 rounded-lg border">
                          <div>
                            <p className="font-medium text-sm">{p.organizationName}</p>
                            <p className="text-xs text-muted-foreground">{p.planName}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-sm">${p.amount?.toFixed(2)} {p.currency}</p>
                            <Badge
                              variant={p.paymentStatus?.toLowerCase() === 'paid' ? 'success' : 'secondary'}
                              className="text-xs mt-1"
                            >
                              {p.paymentStatus}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
